import { Controller } from "@hotwired/stimulus"
import { useDebounce } from 'stimulus-use'

interface Tag {
  name: string
  searchCount: number
  source: string
}

export default class extends Controller {
  static debounces = [{ name: "fetchTags", wait: 1000 }]
  static targets = ["title", "description", "subjobtype", "portaltype", "keywordResults", "tags"]
  static outlets = ["choices"]

  declare titleTarget: HTMLInputElement
  declare keywordResultsTarget: HTMLDivElement
  declare tagsTarget: HTMLInputElement
  declare descriptionTarget: HTMLTextAreaElement
  declare subjobtypeTarget: HTMLSelectElement
  declare portaltypeTargets: HTMLInputElement[]
  declare choicesOutlet: any
  declare hasChoicesOutlet: boolean

  private keywordResult: Tag[] = []
  private boundRenderTags = () => this.renderTags()

  connect() {
    useDebounce(this, "fetchTags")
    this.fetchTags()
    this.titleTarget.addEventListener("input", () => this.fetchTags())
    this.descriptionTarget.addEventListener("input", () => this.fetchTags())
    this.subjobtypeTarget.addEventListener("change", () => this.fetchTags())
    this.portaltypeTargets.forEach((el) => el.addEventListener("change", () => this.fetchTags()))
  }

  choicesOutletConnected() {
    // Wait for choices to initialize (outlet connects before target controller's connect() finishes)
    requestAnimationFrame(() => {
      const choices = this.choicesOutlet.choices
      if (!choices) return
      const el = choices.passedElement.element
      el.addEventListener("addItem", this.boundRenderTags)
      el.addEventListener("removeItem", this.boundRenderTags)
    })
  }

  choicesOutletDisconnected() {
    const el = this.choicesOutlet?.choices?.passedElement?.element
    if (el) {
      el.removeEventListener("addItem", this.boundRenderTags)
      el.removeEventListener("removeItem", this.boundRenderTags)
    }
  }

  fetchTags() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    fetch("/next/job-form/auto_tag", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        title: this.titleTarget.value,
        text: this.descriptionTarget.value,
        fid: this.fId(),
        portal_type: this.portaltypeTargets.find((el) => el.checked)?.value
      })
    })
    .then((response) => response.json())
    .then((data) => {
      this.keywordResult = data.keyword_info.suggestions
      this.renderTags()
    })
  }

  selectedTags(): string[] {
    if (!this.hasChoicesOutlet) return []
    return this.choicesOutlet.choices.getValue(true)
  }

  renderTags() {
    if (this.keywordResult.length === 0) {
      this.keywordResultsTarget.innerHTML = ""
      return
    }

    const selected = this.selectedTags()
    const notSelected = this.keywordResult
      .filter((tag) => tag.name && !selected.includes(tag.name))
      .filter((tag, i, self) => self.findIndex(t => t.name === tag.name) === i)

    if (notSelected.length === 0) {
      this.keywordResultsTarget.innerHTML = ""
      return
    }

    const buttons = notSelected.map((tag) => {
      const escaped = JSON.stringify(tag).replace(/'/g, '&#39;')
      return `<button data-tag='${escaped}' data-action="click->walk-in-job#addTag:prevent" class="btn mb-1 btn-outline-dark">${tag.name}</button>`
    }).join(" ")

    this.keywordResultsTarget.innerHTML =
      `<small class='mt-1 text-muted'>Vorschläge für Schlagworte auf Basis Ihres Titels / Beschreibung (Anklicken zum Übernehmen):</small><br>${buttons}`
  }

  addTag(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    if (!button?.dataset.tag) return
    const tag = JSON.parse(button.dataset.tag) as Tag
    this.choicesOutlet.choices.setValue([tag.name])
    this.renderTags()
  }

  fId(): string {
    const value = this.subjobtypeTarget.selectedOptions[0]?.value
    return ["fachkraft", "fuehrungskraft", "freelancer", "hilfskraft"].includes(value) ? "4" : "5"
  }
}
